/**
 * Planning Engine Module — Pure calculation engine
 * v21.1: Global Planning Mode, Item Planning Basis, Decomposition Maturity
 * v22:   Team Participation Requirements
 *
 * NO Supabase imports. Pure deterministic functions only.
 */

import type {
  Scenario,
  Priority,
  WorkUnit,
  Dependency,
  PriorityDependency,
  CapacityModelRow,
  PriorityCapacityRow,
  TeamParticipation,
  PlanRecord,
  PriorityPlanRecord,
  PlanDecision,
  DependencyStatus,
  ReadinessStatus,
  CapacityStatus,
  ParticipationCapacityStatus,
  CapabilityStatus,
  CommitmentStatus,
  DecisionWithParticipation,
  PrioritySummary,
  PriorityFeasibility,
  ResourceSummary,
  ExecutiveSummary,
  PlanningWarning,
  PlanningEngineInput,
  PlanningEngineOutput,
  PlanningRelationshipGroup,
  ConsistencyCheck,
  QualificationInput,
} from './planningEngineModule.types';
import {
  explainQualificationGap,
  QualifiedCapacityLedger,
} from './planningQualifiedCapacity';

// ---------------------------------------------------------------------------
// §1 — Work units required
// ---------------------------------------------------------------------------

export function calculateWorkUnitsRequired(
  workUnit: WorkUnit,
  scenario: Scenario,
  /**
   * The uncertainty multiplier of the work unit's execution recipe, when the
   * caller has resolved it. Used only under the 'work_type' basis; omitted or
   * unresolved means 1 (no adjustment), which is also what 'buffer' scenarios
   * want.
   */
  uncertaintyMultiplier?: number,
): number {
  const rawHours = workUnit.countedEffortHours ?? workUnit.effortEstimateHours;
  if (rawHours <= 0) return 0;
  // Under 'work_type' the uncertainty allowance is carried here, on demand, per
  // work type — and the scenario's flat buffer is not applied to supply. Under
  // 'buffer' it is the other way round. Taking both would charge the same
  // uncertainty twice; taking one on each side of the product is what made the
  // engine and the plan of record disagree about identical work.
  const multiplier =
    scenario.uncertaintyBasis === 'work_type' &&
    typeof uncertaintyMultiplier === 'number' &&
    uncertaintyMultiplier >= 1
      ? uncertaintyMultiplier
      : 1;
  const effectiveHours = rawHours * multiplier;
  return Math.ceil((effectiveHours / scenario.defaultWorkUnitHours) * 10) / 10;
}

/**
 * The uncertainty multiplier for a work unit's execution recipe, or undefined.
 *
 * Passed through the engine rather than looked up, because this module is pure:
 * the caller resolves the recipes once and hands the map in. Under the 'buffer'
 * basis nothing reads it.
 */
export type RecipeMultipliers = ReadonlyMap<string, number>;

function multiplierFor(workUnit: WorkUnit, multipliers?: RecipeMultipliers): number | undefined {
  if (!multipliers || !workUnit.resolvedRecipeId) return undefined;
  return multipliers.get(workUnit.resolvedRecipeId);
}

// ---------------------------------------------------------------------------
// §2 — Readiness
// ---------------------------------------------------------------------------

const READY_STATES: ReadonlySet<string> = new Set(['done', 'in_progress', 'selected_for_dev']);

export function calculateReady(workUnit: WorkUnit): boolean {
  if (READY_STATES.has(workUnit.state)) return true;
  if (workUnit.includedInPlan === true) return true;
  if (workUnit.state === 'ready') return true;
  return false;
}

export function calculateReadinessStatus(workUnit: WorkUnit, _scenario: Scenario): ReadinessStatus {
  return calculateReady(workUnit) ? 'Ready' : 'Not Ready';
}

// ---------------------------------------------------------------------------
// §3 — Work-unit dependency resolution
// ---------------------------------------------------------------------------

export function calculateDependencySatisfied(
  dependency: Dependency,
  workUnitById: Map<string, WorkUnit>,
): boolean {
  const prereq = workUnitById.get(dependency.toWorkUnitId);
  if (!prereq) return false;
  return READY_STATES.has(prereq.state) || prereq.state === 'ready';
}

export function calculateDependenciesSatisfied(
  workUnit: WorkUnit,
  dependencies: Dependency[],
  workUnitById: Map<string, WorkUnit>,
): boolean {
  const blocking = dependencies.filter(
    (d) => d.fromWorkUnitId === workUnit.id && d.blocking && d.dependencyType === 'Hard',
  );
  if (blocking.length === 0) return true;
  return blocking.every((d) => calculateDependencySatisfied(d, workUnitById));
}

export function calculateDependencyStatus(
  workUnit: WorkUnit,
  _scenario: Scenario,
  dependencies: Dependency[],
  workUnitById: Map<string, WorkUnit>,
): DependencyStatus {
  const blocking = dependencies.filter(
    (d) => d.fromWorkUnitId === workUnit.id && d.blocking && d.dependencyType === 'Hard',
  );
  if (blocking.length === 0) return 'No Dependencies';
  const allSatisfied = blocking.every((d) => calculateDependencySatisfied(d, workUnitById));
  return allSatisfied ? 'Satisfied' : 'Unsatisfied';
}

// ---------------------------------------------------------------------------
// §4 — Priority-level dependency resolution (v21.1)
// ---------------------------------------------------------------------------

export function calculatePriorityDependencyStatus(
  priority: Priority,
  priorityDependencies: PriorityDependency[],
  priorityDecisions: Map<string, PlanDecision>,
): DependencyStatus {
  const blocking = priorityDependencies.filter(
    (d) => d.fromPriorityId === priority.id && d.blocking && d.dependencyType === 'Hard',
  );
  if (blocking.length === 0) return 'No Dependencies';
  const allSatisfied = blocking.every((d) => {
    const prereqDecision = priorityDecisions.get(d.toPriorityId);
    return prereqDecision === 'Included';
  });
  return allSatisfied ? 'Satisfied' : 'Unsatisfied';
}

// ---------------------------------------------------------------------------
// §5 — Capacity lookup
// ---------------------------------------------------------------------------

export function getResourceCapacityWU(
  scenarioId: string,
  resourceType: string,
  capacityRows: CapacityModelRow[],
): number {
  return capacityRows
    .filter((r) => r.scenarioId === scenarioId && r.resourceType === resourceType)
    .reduce((sum, r) => sum + r.workUnitCapacity, 0);
}

export function getPriorityResourceCapacityWU(
  scenarioId: string,
  resourceType: string,
  rows: PriorityCapacityRow[],
): number {
  return rows
    .filter((r) => r.scenarioId === scenarioId && r.resourceType === resourceType)
    .reduce((sum, r) => sum + r.workUnitCapacity, 0);
}

/** Default resource pool when a priority carries no explicit resource type. */
const DEFAULT_RESOURCE_TYPE = 'engineering';

/** Resource pool a priority consumes from. */
export function resolvePriorityResourceType(priority: Priority): string {
  return priority.priorityResourceType || DEFAULT_RESOURCE_TYPE;
}

/**
 * Resource pool a work unit consumes from. A work unit with no meaningful
 * profile (e.g. an `unknown` placeholder materialized on sync from a live
 * item) inherits its parent priority's resource type, so it plans against a
 * real capacity pool instead of always falling to `No Capacity Model`.
 */
export function resolveWorkUnitResourceType(
  workUnit: WorkUnit,
  priorityById: Map<string, Priority>,
): string {
  if (workUnit.workProfile && workUnit.workProfile !== 'unknown') return workUnit.workProfile;
  const parent = workUnit.priorityId ? priorityById.get(workUnit.priorityId) : undefined;
  return parent ? resolvePriorityResourceType(parent) : (workUnit.workProfile || 'unknown');
}

// ---------------------------------------------------------------------------
// §6 — v21.1 Demand calculation
// ---------------------------------------------------------------------------

/**
 * Calculates the total work-unit demand already identified via child work units.
 */
export function calculateIdentifiedWorkUnitsTotal(
  priority: Priority,
  workUnits: WorkUnit[],
  scenario: Scenario,
  multipliers?: RecipeMultipliers,
): number {
  const children = workUnits.filter((wu) => wu.priorityId === priority.id);
  return children.reduce(
    (sum, wu) => sum + calculateWorkUnitsRequired(wu, scenario, multiplierFor(wu, multipliers)),
    0,
  );
}

/**
 * Item Planning Demand — what the item's own planning basis implies.
 * Returns null when the basis cannot be satisfied (e.g. Work Units basis with no WUs).
 */
export function calculateItemPlanningDemand(
  priority: Priority,
  identifiedTotal: number,
): number | null {
  switch (priority.itemPlanningBasis) {
    case 'priority_estimate':
      return priority.priorityLevelWuEstimate ?? null;
    case 'work_units':
      return identifiedTotal;
    case 'hybrid_remaining':
      // Hybrid = known WUs + remaining undecomposed estimate (explicit judgment)
      return identifiedTotal + (priority.remainingWuEstimate ?? 0);
  }
}

/**
 * Active Mode Planning Demand — what the current global run actually uses.
 * For Hybrid global mode, defers to the item-level planning demand.
 */
export function calculateActiveModeplanningDemand(
  priority: Priority,
  identifiedTotal: number,
  globalMode: Scenario['planningMode'],
): number | null {
  switch (globalMode) {
    case 'priority_estimate':
      return priority.priorityLevelWuEstimate ?? null;
    case 'work_units':
      return identifiedTotal;
    case 'hybrid_remaining':
      return calculateItemPlanningDemand(priority, identifiedTotal);
  }
}

// ---------------------------------------------------------------------------
// §7 — v21.1 Consistency check
// ---------------------------------------------------------------------------

export function checkPlanningConsistency(priority: Priority): ConsistencyCheck {
  const { workDefinitionStatus, itemPlanningBasis } = priority;

  if (workDefinitionStatus === 'not_decomposed') {
    if (itemPlanningBasis === 'priority_estimate') return { status: 'OK', message: 'Not decomposed, using priority estimate.' };
    if (itemPlanningBasis === 'work_units') return { status: 'Invalid', message: 'Work Units basis requires decomposition. No work units defined.' };
    if (itemPlanningBasis === 'hybrid_remaining') return { status: 'Review', message: 'Hybrid basis with no decomposition will use 0 known WUs + remaining estimate.' };
  }

  if (workDefinitionStatus === 'partially_decomposed') {
    if (itemPlanningBasis === 'priority_estimate') return { status: 'OK', message: 'Partially decomposed, using priority estimate (decomposition detail ignored).' };
    if (itemPlanningBasis === 'work_units') return { status: 'Review', message: 'Work Units basis with partial decomposition: remaining work may be excluded from plan.' };
    if (itemPlanningBasis === 'hybrid_remaining') return { status: 'OK', message: 'Hybrid basis: known WUs + remaining estimate.' };
  }

  if (workDefinitionStatus === 'fully_decomposed') {
    if (itemPlanningBasis === 'priority_estimate') return { status: 'Review', message: 'Fully decomposed but using priority estimate: work unit detail is being ignored.' };
    if (itemPlanningBasis === 'work_units') return { status: 'OK', message: 'Fully decomposed, planning from work units.' };
    if (itemPlanningBasis === 'hybrid_remaining') return { status: 'OK', message: 'Fully decomposed; remaining estimate should be 0.' };
  }

  return { status: 'OK', message: '' };
}

// ---------------------------------------------------------------------------
// §8 — v22 Participation
// ---------------------------------------------------------------------------

export function calculateParticipationFields(
  participationRows: TeamParticipation[],
): {
  count: number;
  capacityRequiredWu: number;
  participationCapacityStatus: ParticipationCapacityStatus;
  capabilityStatus: CapabilityStatus;
  commitmentStatus: CommitmentStatus;
  participationRisk: string | null;
} {
  if (participationRows.length === 0) {
    return {
      count: 0,
      capacityRequiredWu: 0,
      participationCapacityStatus: 'No Requirements',
      capabilityStatus: 'No Requirements',
      commitmentStatus: 'No Requirements',
      participationRisk: null,
    };
  }

  const capacityRequiredWu = participationRows.reduce((s, r) => s + (r.capacityRequiredWu ?? 0), 0);

  // Tri-state aggregation: any 'no' → Not Confirmed/Missing/Not Committed
  //                        any 'partial' (and no 'no') → Partially Confirmed/Partial/Partially Committed
  //                        all 'yes' → Confirmed/Present/Committed
  const capacityValues = participationRows.map((r) => r.capacityConfirmed);
  const capabilityValues = participationRows.map((r) => r.capabilityPresent);
  const commitmentValues = participationRows.map((r) => r.committed);

  const participationCapacityStatus: ParticipationCapacityStatus =
    capacityValues.some((v) => v === 'no') ? 'Not Confirmed'
    : capacityValues.some((v) => v === 'partial') ? 'Partially Confirmed'
    : 'Confirmed';

  const capabilityStatus: CapabilityStatus =
    capabilityValues.some((v) => v === 'no') ? 'Missing'
    : capabilityValues.some((v) => v === 'partial') ? 'Partial'
    : 'Present';

  const commitmentStatus: CommitmentStatus =
    commitmentValues.some((v) => v === 'no') ? 'Not Committed'
    : commitmentValues.some((v) => v === 'partial') ? 'Partially Committed'
    : 'Committed';

  const risks = participationRows
    .filter((r) => r.riskIfUnavailable)
    .map((r) => r.riskIfUnavailable as string);
  const participationRisk = risks.length > 0 ? risks.join('; ') : null;

  return {
    count: participationRows.length,
    capacityRequiredWu,
    participationCapacityStatus,
    capabilityStatus,
    commitmentStatus,
    participationRisk,
  };
}

export function calculateDecisionWithParticipation(
  planDecision: PlanDecision,
  participationCapacityStatus: ParticipationCapacityStatus,
  capabilityStatus: CapabilityStatus,
  commitmentStatus: CommitmentStatus,
): DecisionWithParticipation {
  // Non-included decisions pass through unchanged
  if (planDecision !== 'Included') {
    return planDecision as DecisionWithParticipation;
  }

  // Included — layer participation rules in priority order
  if (capabilityStatus === 'Missing') return 'Blocked - Capability';
  if (participationCapacityStatus === 'Not Confirmed' || participationCapacityStatus === 'Partially Confirmed') return 'At Risk - Capacity';
  if (commitmentStatus === 'Not Committed' || commitmentStatus === 'Partially Committed') return 'At Risk - Participation';
  return 'Included';
}

// ---------------------------------------------------------------------------
// §8b — Relationship group rules (Package / Competing)
// ---------------------------------------------------------------------------

/**
 * A group rule the plan violates, and the priorities it makes unachievable.
 */
export interface GroupRuleViolation {
  groupId: string;
  rule: 'package' | 'exclusive' | 'duplicate';
  /** Priorities that were Included but cannot stand given the violation. */
  blockedPriorityIds: string[];
  reason: string;
}

/**
 * Applies Package and Competing rules to a settled decision map.
 *
 * A group violation is treated exactly like an unsatisfied hard dependency —
 * it makes an otherwise-achievable priority **not achievable** — and the engine
 * never resolves it on the user's behalf:
 *
 * - **Package** is all-or-nothing. If any member cannot be done in this plan
 *   (deferred, blocked, not ready, or not in scope at all), then none of them
 *   can, so every Included member is blocked. The engine does NOT pull the
 *   missing members above the line; that would silently spend capacity nobody
 *   agreed to.
 * - **Competing** is at-most-one. If two or more members are Included the
 *   engine does NOT choose — it blocks all of them so a human picks.
 *
 * These conflicts should normally be resolved upstream in Prioritization and
 * rarely reach a plan. But going straight to Planning is a supported path, and
 * a planner that quietly repairs a broken group would be misreporting what the
 * organisation can actually deliver.
 *
 * Pure and order-independent: callers re-run it until the decisions settle,
 * because blocking a priority can invalidate a dependent that was only
 * satisfied by it.
 */
export function evaluateGroupRules(
  groups: PlanningRelationshipGroup[],
  decisions: Map<string, PlanDecision>,
  /** Priorities actually present in this plan; members outside it cannot be delivered here. */
  inScopePriorityIds: Set<string>,
  /**
   * Priorities planned at work-unit grain with some, but not all, of their work
   * units Included. Such a priority is not deliverable (so it breaks a package),
   * yet it is holding included work (so it counts toward at-most-one, and its
   * included work is blocked along with everyone else's).
   */
  partiallyIncludedPriorityIds: ReadonlySet<string> = new Set(),
): GroupRuleViolation[] {
  const violations: GroupRuleViolation[] = [];

  for (const group of groups) {
    const members = group.memberPriorityIds;
    if (members.length < 2) continue;

    const fullyIncluded = members.filter((id) => decisions.get(id) === 'Included');
    // Everything holding included work: what a violation blocks, and what
    // at-most-one counts.
    const included = members.filter(
      (id) => decisions.get(id) === 'Included' || partiallyIncludedPriorityIds.has(id),
    );
    if (included.length === 0) continue;

    const name = group.label ? `"${group.label}"` : 'this group';

    if (group.rule === 'package') {
      const unavailable = members.filter((id) => !fullyIncluded.includes(id));
      if (unavailable.length === 0) continue;

      const outOfScope = unavailable.filter((id) => !inScopePriorityIds.has(id));
      const detail =
        outOfScope.length > 0
          ? `${outOfScope.length} member(s) are not in this plan's scope`
          : `${unavailable.length} member(s) could not be included`;

      violations.push({
        groupId: group.id,
        rule: 'package',
        blockedPriorityIds: included,
        reason:
          `Package ${name} must move together — ${detail}. ` +
          'Change the ranking so the whole package fits, or remove it from this plan.',
      });
      continue;
    }

    // exclusive / duplicate — at most one may be committed.
    //
    // Identical arithmetic, deliberately different wording: Competing is a
    // choice between real alternatives, whereas a duplicate cluster means the
    // plan is counting one piece of work twice. Telling someone to "choose
    // which one to commit to" when they are looking at the same thing written
    // two ways is not useful.
    if (included.length > 1) {
      violations.push({
        groupId: group.id,
        rule: group.rule,
        blockedPriorityIds: included,
        reason:
          group.rule === 'duplicate'
            ? `${name} describes the same underlying need — ${included.length} copies of it are ` +
              'currently included, so this plan counts that work more than once. ' +
              'Keep one and deprioritise the rest, or resolve the duplicates in Prioritization.'
            : `Competing set ${name} allows at most one — ${included.length} are currently included. ` +
              'Choose which one to commit to and deprioritise the others.',
      });
    }
  }

  return violations;
}

// ---------------------------------------------------------------------------
// §9 — Decision reason text
// ---------------------------------------------------------------------------

export function getDecisionReason(decision: PlanDecision): string {
  switch (decision) {
    case 'Included':
      return 'Work unit fits within available capacity and all prerequisites are met.';
    case 'Out - capacity':
      return 'No remaining capacity for this resource type in this scenario.';
    case 'Deferred - No Qualified Capacity':
      // Always overwritten by the ledger with a reason that names the failing
      // dimension. This generic form only shows if a caller asks for the text
      // without a shortfall to hand.
      return 'Capacity remains in this pool, but not with anyone who meets this work’s requirements.';
    case 'Blocked':
      return 'One or more hard blocking dependencies are not yet satisfied.';
    case 'Not Ready':
      return 'Work unit is not in a ready state and is not included in plan.';
    case 'Needs Sizing':
      return 'Work unit has no effort estimate yet — set an estimate to plan it.';
    case 'No Capacity Model':
      return 'No capacity row defined for this resource type in the active scenario.';
    case 'No Work Units Defined':
      return 'Work Units mode is active but this priority has no child work units defined.';
  }
}

export function getPriorityDecisionReason(decision: PlanDecision): string {
  switch (decision) {
    case 'Included':
      return 'Priority fits within available capacity and all prerequisites are met.';
    case 'Out - capacity':
      return 'No remaining capacity for this priority in this scenario.';
    case 'Deferred - No Qualified Capacity':
      return 'Capacity remains in this pool, but not with anyone who meets this priority’s requirements.';
    case 'Blocked':
      return 'One or more hard blocking priority dependencies are not yet satisfied.';
    case 'Not Ready':
      return 'Priority has no plannable demand defined.';
    case 'Needs Sizing':
      return 'Priority has no size estimate yet — set a work-unit estimate to plan it.';
    case 'No Capacity Model':
      return 'No priority-level capacity model defined for this scenario.';
    case 'No Work Units Defined':
      return 'Work Units mode is active but this priority has no child work units defined.';
  }
}

// ---------------------------------------------------------------------------
// §10 — Sort helpers
// ---------------------------------------------------------------------------

export function sortWorkUnitsForPlanning(
  workUnits: WorkUnit[],
  priorityById: Map<string, Priority>,
): WorkUnit[] {
  return [...workUnits].sort((a, b) => {
    const rankA = a.constraintAdjustedRank ?? Infinity;
    const rankB = b.constraintAdjustedRank ?? Infinity;
    if (rankA !== rankB) return rankA - rankB;

    const prA = a.priorityId ? (priorityById.get(a.priorityId)?.forceRank ?? Infinity) : Infinity;
    const prB = b.priorityId ? (priorityById.get(b.priorityId)?.forceRank ?? Infinity) : Infinity;
    if (prA !== prB) return prA - prB;

    return a.createdAt.localeCompare(b.createdAt);
  });
}

export function sortPrioritiesForPlanning(priorities: Priority[]): Priority[] {
  return [...priorities].sort((a, b) => {
    const rankA = a.forceRank ?? Infinity;
    const rankB = b.forceRank ?? Infinity;
    if (rankA !== rankB) return rankA - rankB;
    return a.createdAt.localeCompare(b.createdAt);
  });
}

// ---------------------------------------------------------------------------
// §11 — Work-unit planning loop (used in Work Units and Hybrid modes)
// ---------------------------------------------------------------------------

function runWorkUnitPlanningLoop(
  workUnits: WorkUnit[],
  scenario: Scenario,
  dependencies: Dependency[],
  capacityRows: CapacityModelRow[],
  teamParticipation: TeamParticipation[],
  priorityById: Map<string, Priority>,
  /**
   * Phase B. When present, the pool lookup narrows per work unit instead of per
   * resource type. Absent — the overwhelmingly common case — leaves every line
   * below on its original arithmetic.
   */
  ledger: QualifiedCapacityLedger | null = null,
  /** Recipe uncertainty multipliers; read only under the 'work_type' basis. */
  multipliers?: RecipeMultipliers,
): PlanRecord[] {
  const workUnitById = new Map(workUnits.map((wu) => [wu.id, wu]));

  const demandByResource = new Map<string, number>();

  const capacityByResource = new Map<string, number>();
  const resourceTypesWithModel = new Set<string>();
  for (const row of capacityRows) {
    if (row.scenarioId !== scenario.id) continue;
    resourceTypesWithModel.add(row.resourceType);
    capacityByResource.set(
      row.resourceType,
      (capacityByResource.get(row.resourceType) ?? 0) + row.workUnitCapacity,
    );
  }

  const sorted = sortWorkUnitsForPlanning(workUnits, priorityById);
  const planRecords: PlanRecord[] = [];

  for (const wu of sorted) {
    const wuRequired = calculateWorkUnitsRequired(wu, scenario, multiplierFor(wu, multipliers));
    const readinessStatus = calculateReadinessStatus(wu, scenario);
    const dependencyStatus = calculateDependencyStatus(wu, scenario, dependencies, workUnitById);

    const resourceType = resolveWorkUnitResourceType(wu, priorityById);
    const hasCapacityModel = resourceTypesWithModel.has(resourceType);
    const totalCapacity = capacityByResource.get(resourceType) ?? 0;
    const currentDemand = demandByResource.get(resourceType) ?? 0;
    const remainingCapacity = totalCapacity - currentDemand;

    const requirements = ledger ? ledger.requirementsForWorkUnit(wu.id, wu.priorityId) : [];
    const verdict =
      ledger && hasCapacityModel
        ? ledger.evaluate({
            resourceType,
            requiredWU: wuRequired,
            requirements,
            priorityId: wu.priorityId,
            grain: 'work_unit',
          })
        : null;
    let qualificationReason: string | null = null;

    let capacityStatus: CapacityStatus;
    if (!hasCapacityModel) {
      capacityStatus = 'No Capacity Model';
    } else if (verdict) {
      if (verdict.fits) {
        capacityStatus = 'Has Capacity';
      } else if (verdict.shortfall && verdict.poolHasRoom) {
        // The discriminating case: the pool shows surplus and this work still
        // cannot be done, because nobody with room meets its requirements.
        capacityStatus = 'No Qualified Capacity';
        qualificationReason = explainQualificationGap(
          verdict.shortfall,
          resourceType,
          verdict.poolRemainingWU,
        );
      } else {
        capacityStatus = 'No Capacity';
      }
    } else if (remainingCapacity >= wuRequired) {
      capacityStatus = 'Has Capacity';
    } else {
      capacityStatus = 'No Capacity';
    }

    let planDecision: PlanDecision;
    const isBlocked = dependencyStatus === 'Unsatisfied';
    const isNotReady = readinessStatus === 'Not Ready';

    if (isBlocked && !scenario.includeBlocked) {
      planDecision = 'Blocked';
    } else if (isNotReady && !scenario.includeNotReady) {
      planDecision = 'Not Ready';
    } else if (wuRequired === 0) {
      // Ready and unblocked, but carries no effort estimate — it needs sizing
      // before it can consume capacity. Distinct from Not Ready (a work state).
      planDecision = 'Needs Sizing';
    } else if (capacityStatus === 'No Capacity Model') {
      planDecision = 'No Capacity Model';
    } else if (capacityStatus === 'No Qualified Capacity') {
      planDecision = 'Deferred - No Qualified Capacity';
    } else if (capacityStatus === 'No Capacity') {
      planDecision = 'Out - capacity';
    } else {
      planDecision = 'Included';
    }

    const runningDemandBefore = currentDemand;
    const booksDemand =
      planDecision === 'Included' ||
      planDecision === 'Out - capacity' ||
      planDecision === 'Deferred - No Qualified Capacity';
    if (booksDemand) {
      demandByResource.set(resourceType, currentDemand + wuRequired);
      ledger?.consume({
        resourceType,
        amountWU: wuRequired,
        requirements,
        priorityId: wu.priorityId,
        grain: 'work_unit',
      });
    }
    const runningDemandAfter = demandByResource.get(resourceType) ?? currentDemand;

    // v22 participation — include both WU-specific rows AND priority-level rows
    // (workUnitId === null means "applies to all WUs for this priority")
    const wuParticipation = teamParticipation.filter(
      (p) => p.workUnitId === wu.id ||
             (p.workUnitId === null && p.priorityId === wu.priorityId && wu.priorityId !== null),
    );
    const participation = calculateParticipationFields(wuParticipation);
    const decisionWithParticipation = calculateDecisionWithParticipation(
      planDecision,
      participation.participationCapacityStatus,
      participation.capabilityStatus,
      participation.commitmentStatus,
    );

    planRecords.push({
      workUnit: wu,
      priority: wu.priorityId ? (priorityById.get(wu.priorityId) ?? null) : null,
      workUnitsRequired: wuRequired,
      dependencyStatus,
      readinessStatus,
      capacityStatus,
      planDecision,
      decisionReason: qualificationReason ?? getDecisionReason(planDecision),
      runningDemandBefore,
      runningDemandAfter,
      participationRequirementCount: participation.count,
      participationCapacityRequiredWu: participation.capacityRequiredWu,
      participationCapacityStatus: participation.participationCapacityStatus,
      capabilityStatus: participation.capabilityStatus,
      commitmentStatus: participation.commitmentStatus,
      participationRisk: participation.participationRisk,
      decisionWithParticipation,
    });
  }

  return planRecords;
}

// ---------------------------------------------------------------------------
// §12 — Priority-level planning loop (used in Priority Estimate mode)
// ---------------------------------------------------------------------------

function runPriorityPlanningLoop(
  priorities: Priority[],
  scenario: Scenario,
  workUnits: WorkUnit[],
  priorityDependencies: PriorityDependency[],
  priorityCapacityRows: PriorityCapacityRow[],
  teamParticipation: TeamParticipation[],
  fallbackCapacityRows: CapacityModelRow[] = [],
  relationshipGroups: PlanningRelationshipGroup[] = [],
  ledger: QualifiedCapacityLedger | null = null,
): PriorityPlanRecord[] {
  const sorted = sortPrioritiesForPlanning(priorities);

  // Use priority-level capacity if defined; fall back to work-unit capacity rows
  // so that capacity added via the Capacity tab works for all planning modes.
  const hasDedicatedCapacity = priorityCapacityRows.some((r) => r.scenarioId === scenario.id);
  const effectiveCapacityRows: Array<{ scenarioId: string; resourceType: string; workUnitCapacity: number }> =
    hasDedicatedCapacity ? priorityCapacityRows : fallbackCapacityRows;

  const capacityByResource = new Map<string, number>();
  const resourceTypesWithModel = new Set<string>();
  for (const row of effectiveCapacityRows) {
    if (row.scenarioId !== scenario.id) continue;
    resourceTypesWithModel.add(row.resourceType);
    capacityByResource.set(
      row.resourceType,
      (capacityByResource.get(row.resourceType) ?? 0) + row.workUnitCapacity,
    );
  }

  const hasAnyPriorityCapacity = resourceTypesWithModel.size > 0;

  const demandByResource = new Map<string, number>();
  const priorityDecisions = new Map<string, PlanDecision>();
  const records: PriorityPlanRecord[] = [];

  for (const priority of sorted) {
    const identifiedTotal = calculateIdentifiedWorkUnitsTotal(priority, workUnits, scenario);
    const itemPlanningDemand = calculateItemPlanningDemand(priority, identifiedTotal);
    const activeModeplanningDemand = calculateActiveModeplanningDemand(priority, identifiedTotal, scenario.planningMode);
    const consistencyCheck = checkPlanningConsistency(priority);

    const demand = activeModeplanningDemand ?? 0;

    const dependencyStatus = calculatePriorityDependencyStatus(priority, priorityDependencies, priorityDecisions);

    // Use the priority's own resource type to consume from the matching capacity pool
    const resourceType = resolvePriorityResourceType(priority);
    const hasCapacityModel = hasAnyPriorityCapacity && resourceTypesWithModel.has(resourceType);

    // Phase B — the pool lookup narrows to the slice that qualifies for THIS
    // priority. Priority grain matters as much as work-unit grain: Priority
    // Estimate is the mode most scenarios actually run in.
    const requirements = ledger ? ledger.requirementsForPriority(priority.id) : [];
    const verdict =
      ledger && hasCapacityModel
        ? ledger.evaluate({
            resourceType,
            requiredWU: demand,
            requirements,
            priorityId: priority.id,
            grain: 'priority',
          })
        : null;
    let qualificationReason: string | null = null;

    let capacityStatus: CapacityStatus;
    if (!hasCapacityModel) {
      capacityStatus = 'No Capacity Model';
    } else if (verdict) {
      if (verdict.fits) {
        capacityStatus = 'Has Capacity';
      } else if (verdict.shortfall && verdict.poolHasRoom) {
        capacityStatus = 'No Qualified Capacity';
        qualificationReason = explainQualificationGap(
          verdict.shortfall,
          resourceType,
          verdict.poolRemainingWU,
        );
      } else {
        capacityStatus = 'No Capacity';
      }
    } else {
      const totalCapacity = capacityByResource.get(resourceType) ?? 0;
      const currentDemand = demandByResource.get(resourceType) ?? 0;
      const remainingCapacity = totalCapacity - currentDemand;
      if (remainingCapacity >= demand) {
        capacityStatus = 'Has Capacity';
      } else {
        capacityStatus = 'No Capacity';
      }
    }

    let planDecision: PlanDecision;
    if (activeModeplanningDemand === null || activeModeplanningDemand === 0) {
      // No plannable demand yet — the priority has not been sized. This is an
      // upstream data gap, not a work-readiness state, so it reads as Needs
      // Sizing rather than Not Ready.
      planDecision = 'Needs Sizing';
    } else if (dependencyStatus === 'Unsatisfied') {
      planDecision = 'Blocked';
    } else if (capacityStatus === 'No Capacity Model') {
      planDecision = 'No Capacity Model';
    } else if (capacityStatus === 'No Qualified Capacity') {
      planDecision = 'Deferred - No Qualified Capacity';
    } else if (capacityStatus === 'No Capacity') {
      planDecision = 'Out - capacity';
    } else {
      planDecision = 'Included';
    }

    priorityDecisions.set(priority.id, planDecision);

    const currentDemandForResource = demandByResource.get(resourceType) ?? 0;
    const runningDemandBefore = currentDemandForResource;
    if (
      planDecision === 'Included' ||
      planDecision === 'Out - capacity' ||
      planDecision === 'Deferred - No Qualified Capacity'
    ) {
      demandByResource.set(resourceType, currentDemandForResource + demand);
      ledger?.consume({
        resourceType,
        amountWU: demand,
        requirements,
        priorityId: priority.id,
        grain: 'priority',
      });
    }
    const runningDemandAfter = demandByResource.get(resourceType) ?? currentDemandForResource;

    // v22 participation
    const pParticipation = teamParticipation.filter((p) => p.priorityId === priority.id && !p.workUnitId);
    const participation = calculateParticipationFields(pParticipation);
    const decisionWithParticipation = calculateDecisionWithParticipation(
      planDecision,
      participation.participationCapacityStatus,
      participation.capabilityStatus,
      participation.commitmentStatus,
    );

    records.push({
      priority,
      identifiedWorkUnitsTotal: identifiedTotal,
      itemPlanningDemand,
      activeModeplanningDemand,
      consistencyCheck,
      dependencyStatus,
      capacityStatus,
      planDecision,
      decisionReason: qualificationReason ?? getPriorityDecisionReason(planDecision),
      runningDemandBefore,
      runningDemandAfter,
      participationRequirementCount: participation.count,
      participationCapacityRequiredWu: participation.capacityRequiredWu,
      participationCapacityStatus: participation.participationCapacityStatus,
      capabilityStatus: participation.capabilityStatus,
      commitmentStatus: participation.commitmentStatus,
      participationRisk: participation.participationRisk,
      decisionWithParticipation,
    });
  }

  applyGroupRulesToPlan([], records, priorityDependencies, relationshipGroups);

  return records;
}

/**
 * Downgrades Included work that violates a Package, Competing or duplicate
 * rule, then settles the knock-on effects. Runs in every planning mode.
 *
 * A priority is planned at one of two grains: its own priority record
 * (Priority Estimate mode, and undecomposed priorities in Hybrid), or its work
 * units (Work Units mode, and decomposed priorities in Hybrid). At work-unit
 * grain a priority counts as Included only when every one of its work units
 * is; blocking it blocks each of its included work units, with the same reason.
 *
 * Runs to a fixpoint rather than in one pass, because blocking a priority has
 * two ripple effects: a hard dependent that was only satisfied by it is no
 * longer satisfiable, and removing a member can newly break a *different*
 * package that shared it. Bounded by the priority count — each iteration blocks
 * at least one priority, so it cannot cycle.
 */
export function applyGroupRulesToPlan(
  planRecords: PlanRecord[],
  priorityRecords: PriorityPlanRecord[],
  priorityDependencies: PriorityDependency[],
  relationshipGroups: PlanningRelationshipGroup[],
  /** Every priority in the run, including ones with no record at either grain. */
  inScopePriorityIds?: Iterable<string>,
): void {
  if (relationshipGroups.length === 0) return;

  const recordById = new Map(priorityRecords.map((r) => [r.priority.id, r]));
  const workUnitRecordsById = new Map<string, PlanRecord[]>();
  for (const record of planRecords) {
    const id = record.priority?.id;
    if (!id || recordById.has(id)) continue;
    const list = workUnitRecordsById.get(id);
    if (list) list.push(record);
    else workUnitRecordsById.set(id, [record]);
  }

  const inScope = new Set<string>([
    ...(inScopePriorityIds ?? []),
    ...recordById.keys(),
    ...workUnitRecordsById.keys(),
  ]);

  const decisions = new Map<string, PlanDecision>();
  const partiallyIncluded = new Set<string>();
  for (const [id, record] of recordById) decisions.set(id, record.planDecision);
  for (const [id, records] of workUnitRecordsById) {
    const firstOut = records.find((r) => r.planDecision !== 'Included');
    decisions.set(id, firstOut ? firstOut.planDecision : 'Included');
    if (firstOut && records.some((r) => r.planDecision === 'Included')) partiallyIncluded.add(id);
  }

  /** Priorities this pass blocked — the only cause it re-settles at work-unit grain. */
  const blockedHere = new Set<string>();

  const block = (priorityId: string, reason: string): boolean => {
    const record = recordById.get(priorityId);
    if (record) {
      if (decisions.get(priorityId) !== 'Included') return false;
      record.planDecision = 'Blocked';
      record.decisionReason = reason;
      record.dependencyStatus = 'Unsatisfied';
      record.decisionWithParticipation = 'Blocked';
    } else {
      const records = workUnitRecordsById.get(priorityId);
      if (!records) return false;
      if (decisions.get(priorityId) !== 'Included' && !partiallyIncluded.has(priorityId)) return false;
      for (const wu of records) {
        if (wu.planDecision !== 'Included') continue;
        wu.planDecision = 'Blocked';
        wu.decisionReason = reason;
        wu.dependencyStatus = 'Unsatisfied';
        wu.decisionWithParticipation = 'Blocked';
      }
      partiallyIncluded.delete(priorityId);
    }
    decisions.set(priorityId, 'Blocked');
    blockedHere.add(priorityId);
    return true;
  };

  for (let pass = 0; pass <= inScope.size; pass += 1) {
    let changed = false;

    for (const violation of evaluateGroupRules(relationshipGroups, decisions, inScope, partiallyIncluded)) {
      for (const id of violation.blockedPriorityIds) {
        if (block(id, violation.reason)) changed = true;
      }
    }

    // A priority whose hard prerequisite just became unachievable cannot stand
    // either — same rule the engine already applies to dependencies, applied to
    // the consequences of a group violation.
    for (const dep of priorityDependencies) {
      if (!dep.blocking || dep.dependencyType !== 'Hard') continue;
      const fromHoldsWork =
        decisions.get(dep.fromPriorityId) === 'Included' || partiallyIncluded.has(dep.fromPriorityId);
      if (!fromHoldsWork) continue;
      if (decisions.get(dep.toPriorityId) === 'Included') continue;
      // The planning loops do not read a priority dependency when either end
      // is planned at work-unit grain, so there this pass only follows blocks
      // it caused itself. Between two priority-grain records it settles the
      // dependency in full, as it always has.
      const bothAtPriorityGrain =
        recordById.has(dep.fromPriorityId) && !workUnitRecordsById.has(dep.toPriorityId);
      if (!bothAtPriorityGrain && !blockedHere.has(dep.toPriorityId)) continue;
      if (block(dep.fromPriorityId, getPriorityDecisionReason('Blocked'))) changed = true;
    }

    if (!changed) return;
  }
}

// ---------------------------------------------------------------------------
// §12b — Qualification ledger construction
// ---------------------------------------------------------------------------

/**
 * Builds the per-unit ledger, or returns null to leave the run on anonymous
 * pools.
 *
 * Requirements without supply to check them against are a data problem, not a
 * reason to fail a run: they are ignored, with a warning, so a workspace that
 * declares requirements before entering its roster gets an explanation instead
 * of an inexplicably empty plan.
 */
function buildQualificationLedger(
  qualification: QualificationInput | undefined,
  warnings: PlanningWarning[],
): QualifiedCapacityLedger | null {
  if (!qualification) return null;

  const attributable = qualification.supply.filter((u) => u.attributable);
  if (attributable.length === 0) {
    if (qualification.requirements.length > 0) {
      warnings.push({
        code: 'REQUIREMENTS_WITHOUT_ROSTER',
        message:
          `${qualification.requirements.length} requirement(s) are declared, but no roster capacity ` +
          'stands behind this scenario\'s pools. Set the scenario\'s capacity source to the roster, ' +
          'or the requirements cannot narrow anything and are ignored.',
      });
    }
    return null;
  }

  return new QualifiedCapacityLedger(qualification);
}

// ---------------------------------------------------------------------------
// §13 — Main engine entry point
// ---------------------------------------------------------------------------

export function runPlanningEngine(input: PlanningEngineInput): PlanningEngineOutput {
  const {
    scenario,
    priorities,
    workUnits,
    dependencies,
    priorityDependencies,
    capacityRows,
    priorityCapacityRows,
    teamParticipation,
    relationshipGroups = [],
    qualification,
    recipeMultipliers,
  } = input;

  const priorityById = new Map(priorities.map((p) => [p.id, p]));
  const warnings: PlanningWarning[] = [];

  // Phase B. Built only when the run actually carries qualified supply — with
  // no ledger, every loop below stays on its original per-resource-type
  // arithmetic, which is what makes the backward-compatibility invariant hold
  // by construction rather than by testing for it.
  const ledger = buildQualificationLedger(qualification, warnings);

  // v21.1 consistency checks — warn on invalid combinations
  for (const p of priorities) {
    const check = checkPlanningConsistency(p);
    if (check.status === 'Invalid') {
      warnings.push({ code: 'CONSISTENCY_INVALID', message: check.message, priorityId: p.id });
    } else if (check.status === 'Review') {
      warnings.push({ code: 'CONSISTENCY_REVIEW', message: check.message, priorityId: p.id });
    }
  }

  let planRecords: PlanRecord[] = [];
  let priorityPlanRecords: PriorityPlanRecord[] = [];

  switch (scenario.planningMode) {
    case 'priority_estimate':
      // Plan at the priority grain — no work-unit-level records.
      // capacityRows (from Capacity tab) are passed as fallback so the
      // Capacity tab works for all planning modes without a separate setup step.
      priorityPlanRecords = runPriorityPlanningLoop(
        priorities, scenario, workUnits, priorityDependencies, priorityCapacityRows, teamParticipation, capacityRows,
        relationshipGroups, ledger,
      );
      break;

    case 'work_units':
      // Plan at work-unit grain
      // Priorities without WUs get a warning AND a No Work Units Defined priority record
      {
        const priorityIdsWithWU = new Set(workUnits.map((wu) => wu.priorityId).filter(Boolean));
        const noWUPriorities: Priority[] = [];
        for (const p of priorities) {
          if (!priorityIdsWithWU.has(p.id)) {
            warnings.push({
              code: 'NO_WORK_UNITS',
              message: `Priority "${p.name}" has no work units defined. It cannot be planned in Work Units mode.`,
              priorityId: p.id,
            });
            noWUPriorities.push(p);
          }
        }
        planRecords = runWorkUnitPlanningLoop(
          workUnits, scenario, dependencies, capacityRows, teamParticipation, priorityById, ledger,
          recipeMultipliers,
        );
        // Append No Work Units Defined placeholder records
        priorityPlanRecords = noWUPriorities.map((p) => ({
          priority: p,
          identifiedWorkUnitsTotal: 0,
          itemPlanningDemand: null,
          activeModeplanningDemand: null,
          consistencyCheck: checkPlanningConsistency(p),
          dependencyStatus: 'No Dependencies' as const,
          planDecision: 'No Work Units Defined' as const,
          decisionReason: 'Work Units mode is active but this priority has no child work units defined.',
          runningDemandBefore: 0,
          runningDemandAfter: 0,
          participationRequirementCount: 0,
          participationCapacityRequiredWu: 0,
          participationCapacityStatus: 'No Requirements' as const,
          capabilityStatus: 'No Requirements' as const,
          commitmentStatus: 'No Requirements' as const,
          participationRisk: null,
          decisionWithParticipation: 'No Work Units Defined' as const,
        }));
      }
      break;

    case 'hybrid_remaining':
      // Hybrid: for each priority, use its item-level planning basis.
      // WU-grain records are generated for priorities that actually have work
      // units. Priority-grain records are generated for the rest.
      //
      // Split by whether the priority actually has work_units rows, not by
      // workDefinitionStatus — that flag can go stale relative to the real
      // rows (e.g. work units edited/deleted without updating the status).
      // Trusting the flag either silently drops a priority from planning
      // entirely (flagged decomposed but its WUs are gone: excluded from both
      // loops) or ignores its real work units in favor of a stale priority-
      // level estimate (flagged not_decomposed but WUs already exist). Same
      // priorityIdsWithWU pattern as the 'work_units' branch above.
      {
        const priorityIdsWithWU = new Set(workUnits.map((wu) => wu.priorityId).filter(Boolean));
        const wusToInclude = workUnits.filter((wu) => wu.priorityId && priorityIdsWithWU.has(wu.priorityId));
        const undecomposedPriorities = priorities.filter((p) => !priorityIdsWithWU.has(p.id));

        // One ledger across both loops in Hybrid mode: a work unit and an
        // undecomposed priority planned in the same run draw on the same
        // people, so they must not each get a private copy of the remainder.
        planRecords = runWorkUnitPlanningLoop(
          wusToInclude, scenario, dependencies, capacityRows, teamParticipation, priorityById, ledger,
          recipeMultipliers,
        );
        priorityPlanRecords = runPriorityPlanningLoop(
          undecomposedPriorities, scenario, workUnits, priorityDependencies, priorityCapacityRows, teamParticipation, capacityRows,
          [], ledger,
        );
      }
      break;
  }

  // Priority Estimate applies the group rules inside its own loop. The other
  // two modes plan some or all priorities at work-unit grain, so the rules run
  // here, across both grains at once.
  if (scenario.planningMode !== 'priority_estimate') {
    applyGroupRulesToPlan(
      planRecords, priorityPlanRecords, priorityDependencies, relationshipGroups,
      priorities.map((p) => p.id),
    );
  }

  return buildPlanningOutput(
    planRecords, priorityPlanRecords, priorities, capacityRows, scenario, warnings,
    priorityCapacityRows,
  );
}

// ---------------------------------------------------------------------------
// §14 — Build output summaries
// ---------------------------------------------------------------------------

/**
 * Decisions that consumed (or tried to consume) capacity, and therefore count
 * as demand against a resource type. Kept in one place so the allocation loops
 * and the utilization readout can never disagree about what a run demanded.
 */
const BOOKS_DEMAND: ReadonlySet<PlanDecision> = new Set<PlanDecision>([
  'Included',
  'Out - capacity',
  'Deferred - No Qualified Capacity',
]);

export function buildPlanningOutput(
  planRecords: PlanRecord[],
  priorityPlanRecords: PriorityPlanRecord[],
  priorities: Priority[],
  capacityRows: CapacityModelRow[],
  scenario: Scenario,
  warnings: PlanningWarning[] = [],
  /**
   * Priority-estimate scenarios keep their capacity in planning_priority_capacity,
   * not planning_capacity, and the inclusion pass already reads it (see
   * `hasDedicatedCapacity`). The resource summary did not, so a run that had
   * correctly fitted 9 items inside a 120 WU budget still reported
   * "engineering · capacity 0.0 · demand 173.0 · utilization 0%" — a readout that
   * contradicts the plan printed directly above it.
   */
  priorityCapacityRows: PriorityCapacityRow[] = [],
): PlanningEngineOutput {
  // --- Priority summaries ---
  const prioritySummaries: PrioritySummary[] = priorities.map((priority) => {
    const records = planRecords.filter((r) => r.priority?.id === priority.id);
    const priorityRecord = priorityPlanRecords.find((r) => r.priority.id === priority.id);

    const requiredWU = priorityRecord?.activeModeplanningDemand
      ?? records.reduce((s, r) => s + r.workUnitsRequired, 0);
    const includedWU = records
      .filter((r) => r.planDecision === 'Included')
      .reduce((s, r) => s + r.workUnitsRequired, 0)
      + (priorityRecord?.planDecision === 'Included' ? (priorityRecord.activeModeplanningDemand ?? 0) : 0);
    const deferredWU = records
      .filter((r) => r.planDecision === 'Out - capacity' || r.planDecision === 'Deferred - No Qualified Capacity')
      .reduce((s, r) => s + r.workUnitsRequired, 0)
      + (priorityRecord?.planDecision === 'Out - capacity' ||
         priorityRecord?.planDecision === 'Deferred - No Qualified Capacity'
        ? (priorityRecord.activeModeplanningDemand ?? 0)
        : 0);
    const blockedOrNotReadyWU = records
      .filter((r) => r.planDecision === 'Blocked' || r.planDecision === 'Not Ready')
      .reduce((s, r) => s + r.workUnitsRequired, 0)
      + (priorityRecord?.planDecision === 'Blocked' || priorityRecord?.planDecision === 'Not Ready'
        ? (priorityRecord.activeModeplanningDemand ?? 0)
        : 0);

    const coveragePercent = requiredWU > 0 ? Math.round((includedWU / requiredWU) * 100) : 0;

    let feasibility: PriorityFeasibility;
    // A priority with no work-unit records is "No Work" — including when it
    // carries only the synthetic 'No Work Units Defined' placeholder record
    // (created above for priorities without WUs) or is unsized ('Needs
    // Sizing'). Without this, such a priority falls through to 0% coverage and
    // is mislabeled 'Not Achievable' when it simply hasn't been sized yet. Real
    // plan decisions (Included / Out - capacity / …) are left untouched.
    if (
      records.length === 0 &&
      (!priorityRecord ||
        priorityRecord.planDecision === 'No Work Units Defined' ||
        priorityRecord.planDecision === 'Needs Sizing')
    ) {
      feasibility = 'No Work';
    } else if (coveragePercent >= 100) {
      feasibility = 'Fully Achievable';
    } else if (coveragePercent > 0) {
      feasibility = 'Partially Achievable';
    } else {
      feasibility = 'Not Achievable';
    }

    const dominantDecision = priorityRecord?.decisionWithParticipation
      ?? (records.length > 0 ? records[records.length - 1].decisionWithParticipation : 'Included');

    const hasParticipationRisk =
      dominantDecision === 'At Risk - Participation' ||
      dominantDecision === 'At Risk - Capacity' ||
      dominantDecision === 'Blocked - Capability';

    const blockedReason =
      (priorityRecord?.planDecision === 'Blocked' ? priorityRecord.decisionReason : null)
      ?? records.find((r) => r.planDecision === 'Blocked')?.decisionReason
      ?? null;

    return {
      priority,
      requiredWU,
      includedWU,
      deferredWU,
      blockedOrNotReadyWU,
      coveragePercent,
      feasibility,
      hasParticipationRisk,
      decisionWithParticipation: dominantDecision,
      blockedReason,
    };
  });

  // --- Resource summaries ---
  // Demand is attributed using the same resource-type resolution the planning
  // loops use, so utilization reconciles with what actually consumed capacity.
  // Priority-grain records (Priority Estimate mode, and undecomposed priorities
  // in Hybrid mode) must be included here — otherwise resource demand reads 0
  // in the mode where no work-unit records exist.
  const priorityById = new Map(priorities.map((p) => [p.id, p]));
  const demandMap = new Map<string, number>();
  for (const record of planRecords) {
    if (BOOKS_DEMAND.has(record.planDecision)) {
      const rt = resolveWorkUnitResourceType(record.workUnit, priorityById);
      demandMap.set(rt, (demandMap.get(rt) ?? 0) + record.workUnitsRequired);
    }
  }
  for (const record of priorityPlanRecords) {
    if (BOOKS_DEMAND.has(record.planDecision)) {
      const rt = resolvePriorityResourceType(record.priority);
      demandMap.set(rt, (demandMap.get(rt) ?? 0) + (record.activeModeplanningDemand ?? 0));
    }
  }

  // Same precedence the inclusion pass uses: dedicated priority-capacity rows win
  // when this scenario has any, otherwise fall back to the shared capacity model.
  const dedicated = priorityCapacityRows.filter((r) => r.scenarioId === scenario.id);
  const capacitySource: Array<{ scenarioId: string; resourceType: string; workUnitCapacity: number }> =
    dedicated.length > 0 ? dedicated : capacityRows;

  const capacityMap = new Map<string, number>();
  for (const row of capacitySource) {
    if (row.scenarioId !== scenario.id) continue;
    capacityMap.set(row.resourceType, (capacityMap.get(row.resourceType) ?? 0) + row.workUnitCapacity);
  }

  const allResourceTypes = new Set([...demandMap.keys(), ...capacityMap.keys()]);
  const resourceSummaries: ResourceSummary[] = Array.from(allResourceTypes).map((rt) => {
    const capacityWU = capacityMap.get(rt) ?? 0;
    const demandWU = demandMap.get(rt) ?? 0;
    const remainingWU = capacityWU - demandWU;
    const utilizationPercent = capacityWU > 0 ? Math.round((demandWU / capacityWU) * 100) : 0;
    return { resourceType: rt, capacityWU, demandWU, remainingWU, utilizationPercent };
  });

  // --- Executive summary ---
  const allPlanDecisions = [
    ...planRecords.map((r) => r.planDecision),
    ...priorityPlanRecords.map((r) => r.planDecision),
  ];
  const allParticipationDecisions = [
    ...planRecords.map((r) => r.decisionWithParticipation),
    ...priorityPlanRecords.map((r) => r.decisionWithParticipation),
  ];

  const includedCount = allPlanDecisions.filter((d) => d === 'Included').length;
  const deferredCount = allPlanDecisions.filter((d) => d === 'Out - capacity').length;
  const noQualifiedCapacityCount = allPlanDecisions.filter(
    (d) => d === 'Deferred - No Qualified Capacity',
  ).length;
  const blockedCount = allPlanDecisions.filter((d) => d === 'Blocked').length;
  const notReadyCount = allPlanDecisions.filter((d) => d === 'Not Ready').length;
  const needsSizingCount = allPlanDecisions.filter((d) => d === 'Needs Sizing').length;
  const noCapacityModelCount = allPlanDecisions.filter((d) => d === 'No Capacity Model').length;
  const participationAtRiskCount = allParticipationDecisions.filter(
    (d) => d === 'At Risk - Participation' || d === 'At Risk - Capacity',
  ).length;
  const participationBlockedCount = allParticipationDecisions.filter(
    (d) => d === 'Blocked - Capability',
  ).length;

  const allFeasibilities = prioritySummaries.map((s) => s.feasibility);
  let overallFeasibility: PriorityFeasibility = 'Fully Achievable';
  if (allFeasibilities.every((f) => f === 'No Work')) {
    overallFeasibility = 'No Work';
  } else if (allFeasibilities.some((f) => f === 'Not Achievable')) {
    overallFeasibility = 'Not Achievable';
  } else if (allFeasibilities.some((f) => f === 'Partially Achievable')) {
    overallFeasibility = 'Partially Achievable';
  }

  const executiveSummary: ExecutiveSummary = {
    totalWorkUnits: planRecords.length + priorityPlanRecords.length,
    includedCount,
    deferredCount,
    blockedCount,
    notReadyCount,
    needsSizingCount,
    noCapacityModelCount,
    noQualifiedCapacityCount,
    overallFeasibility,
    participationAtRiskCount,
    participationBlockedCount,
  };

  return {
    scenario,
    planningMode: scenario.planningMode,
    planRecords,
    priorityPlanRecords,
    prioritySummaries,
    resourceSummaries,
    executiveSummary,
    warnings,
    generatedAt: new Date().toISOString(),
  };
}
